import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormikContextType, FormikProvider } from 'formik';
import useFiltersFetchedData from '../../../../helperHooks/useFiltersFetchedData';
import { CATEGORIES_API_CACHE_KEY } from '../../../../constants';
import FormikTextField from '../../../../components/formikComponents/FormikTextField';
import FormikSelect from '../../../../components/formikComponents/FormikSelect';
import SideFormActions from '../../../../components/createEditComponents/SideFormActions';
import { Category, CategoryErrors, CategoryRaw } from '../../../../types';
import { API_V1_HOSTNAME_KEY } from '../../../../localStorageKeys';

const CategoryForm = ({
  formik,
  cancelAction,
  objectId,
  isSubmitting,
  errors,
  clearError,
}: {
  formik: FormikContextType<any>;
  cancelAction: () => void;
  objectId: string;
  isSubmitting: boolean;
  errors: CategoryErrors;
  clearError: (errorKey: string) => void;
}) => {
  const { t } = useTranslation();

  const { data: categoryRawData, isLoading } = useFiltersFetchedData<{
    Data: CategoryRaw[];
    Metadata: {
      Page: number;
      Limit: number;
      TotalPages: number;
      TotalRecords: number;
      CurrentPageSize: number;
    };
  }>(
    CATEGORIES_API_CACHE_KEY,
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/categories`,
    'categories'
  );

  const categoriesData: Category[] = useMemo(
    () =>
      categoryRawData && categoryRawData?.Data?.length > 0
        ? categoryRawData?.Data?.map((x) => ({
            ...x,
            id: x?.Id,
          }))
        : [],
    [categoryRawData]
  );

  const categoriesAvailable = categoriesData?.map((row) => ({
    label: row.Name,
    value: row.Id?.toString(),
  }));

  const calculateCategoriesToShow = () => {
    if (objectId === '0') {
      return categoriesAvailable;
    }
    return categoriesAvailable?.filter((category) => {
      const categoryFullData = categoriesData?.find(
        (item) => item?.Id?.toString() === category?.value?.toString()
      );

      const currentRowCategoryData = categoriesData?.find(
        (item) => item?.Id?.toString() === objectId.toString()
      );
      const categoriesWithCurrentRowAsParent = categoriesData?.filter(
        (item) => item?.ParentCategoryId?.toString() === currentRowCategoryData?.Id?.toString()
      );

      // Parent category can't be the same category
      // Parent category can't be a category that already has a parent
      // Current row category can't have children
      return (
        category?.value?.toString() !== objectId.toString() &&
        (categoryFullData?.ParentCategoryId === null ||
          categoryFullData?.ParentCategoryId === undefined) &&
        categoriesWithCurrentRowAsParent?.length < 1
      );
    });
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <FormikTextField
          label={t('screens.products.categories.name')}
          id="Name"
          name="Name"
          required
          objectId={objectId}
          formName="categories"
          error={errors?.Name}
          clearError={clearError}
        />
        <FormikSelect
          label={t('screens.products.categories.wetDryCategory')}
          id="IsWet"
          name="IsWet"
          formName="categories"
          required
          options={[
            { label: t('screens.products.categories.wet'), value: 'true' },
            { label: t('screens.products.categories.dry'), value: '' },
          ]}
        />
        <FormikSelect
          label={t('screens.products.categories.parentCategory')}
          id="ParentCategoryId"
          name="ParentCategoryId"
          formName="categories"
          isLoading={isLoading}
          options={calculateCategoriesToShow()}
          loadingText="screens.products.categories.loadingCategories"
        />
        <SideFormActions cancelAction={cancelAction} isSubmitting={isSubmitting} />
      </Form>
    </FormikProvider>
  );
};
export default CategoryForm;
